import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { AudioLines, Timer } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaYoutube } from "react-icons/fa";
import { FaRegCirclePlay } from "react-icons/fa6";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useFontStyle } from "~/hook/useFontStyle";
import useIsQuizLive from "~/hook/useIsQuizLive";
import useLocale from "~/hook/useLocale";
import { Quiz } from "~/interface/satsang-quiz/satsangQuiz";
import { formatTimesForHHMM } from "~/lib/helperFuncation/formatTimesForHHMM";
import { removeSession } from "~/lib/helperFuncation/sessionStorageService";
import { addToCurrentAudio, changeIsAudioPlayState, changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { setIsDialogOpen, setNavigatTo } from "~/lib/store/slice/loginSlice";
import { getSatsangQuizDataThunk } from "~/lib/store/slice/satsangQuizSlice";
import { onPauseVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import PdfIcon from "~/svg/PdfIcon";
import { FALLBACK_IMAGE_URL, ICON_ORANGE_COLOR, QUIZ_COMPLETED, QUIZ_SESSION_KEY, youtubeLinkPrefix } from "~/utils/constant";
import TabComponent from "../shared-components/TabComponent";
import CompletedQuiz from "./CompletedQuiz";
import QuizCountdown from "./QuizCountdown";
import QuizStats from "./QuizStats";
import UpcomingSyllabus from "./UpcomingSyllabus";

// interface TimeLeft {
//   hours: number;
//   minutes: number;
//   seconds: number;
// }

dayjs.extend(duration);

type TabKey = "upcoming" | "completed";
interface TabDef {
  key: TabKey;
  label: string;
}

function QuizHome({ mode = "desktop" }: { mode?: "mobile" | "desktop" }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const searchParamsToken = searchParams.get("token");

  const tabs: TabDef[] = [
    { key: "upcoming", label: t("satsang_quiz.upcoming_quizzes") },
    { key: "completed", label: t("satsang_quiz.completed_quizzes") },
  ];

  const { navigate } = useLocale();
  const dispatch = useAppDispatch();
  const { medium } = useFontStyle();

  const { isUserLogin } = useAppSelector((state) => state.login);
  const { quizData } = useAppSelector((state) => state.satsangQuiz);
  const [selectedTab, setSelectedTab] = useState<TabKey>("upcoming");

  // Callback to refresh quiz data when start time is reached
  const handleQuizStartTimeReached = async () => {
    await dispatch(getSatsangQuizDataThunk());
  };

  // Use custom hook to check if quiz is live based on actual time
  // This doesn't rely on server is_live flag which doesn't update without page refresh
  const { isQuizStarted } = useIsQuizLive({
    date: quizData?.date || "",
    startTime: formatTimesForHHMM(quizData?.start_time ?? "21:00"),
    isLiveFromServer: quizData?.is_live || false,
    onQuizStartTimeReached: handleQuizStartTimeReached, // Callback when start time is reached
  });

  const handleOnPlayNowClick = () => {
    if (!isQuizStarted) {
      toast.error(`Quiz is not live yet`);
      return;
    }

    if ((isUserLogin || (mode === "mobile" && searchParamsToken)) && quizData) {
      removeSession(QUIZ_COMPLETED);
      removeSession(QUIZ_SESSION_KEY);
      navigate(
        mode === "mobile" ? `/satsang-quiz-m/${quizData?.id}?token=${searchParamsToken}` : `/satsang-quiz/${quizData?.id}?token=${searchParamsToken}`,
      );
    } else {
      if (mode === "desktop") {
        dispatch(setIsDialogOpen());
      }
      dispatch(setNavigatTo(mode === "mobile" ? `/satsang-quiz-m/${quizData?.id}?token=${searchParamsToken}` : `/satsang-quiz/${quizData?.id}`));
    }
  };

  const handleOnAudioClick = ({ data }: { data: Quiz }) => {
    dispatch(onPauseVideo());
    const currentAudio = {
      id: data.id,
      img: FALLBACK_IMAGE_URL,
      totalDuration: null,
      artist: null,
      name: data.title,
      audio: data.source_path,
      from: "satsang_quiz",
    };
    dispatch(changeIsAudioPlayState(true));
    dispatch(changeIsPlayingState(true));
    dispatch(addToCurrentAudio(currentAudio));
  };

  return (
    <div className="w-full flex flex-col justify-center items-center gap-9">
      {isUserLogin || (mode === "mobile" && searchParamsToken) ? <QuizStats /> : null}

      <div className="w-full flex justify-center items-center rounded-xl py-5 md:px-4">
        <div className="w-full flex justify-center gap-10 items-center">
          <div className="w-full md:w-[40%] bg-darkOrangeColor flex flex-col gap-4 justify-between items-center rounded-xl py-5 px-8 relative overflow-hidden z-10">
            <img src="/svg/quiz_Shadow.svg" alt="shadow" className="absolute -top-2 right-5 z-0 select-none" />
            <img src="/svg/quiz_Shadow.svg" alt="shadow" className="absolute -top-2 -left-2 z-0 select-none" />

            {quizData ? (
              <>
                {/* Quiz start time and play button section */}
                <div className="w-full md:w-[60%] flex flex-col justify-center items-center gap-3">
                  <span className={cn("body_text_l text-white z-10", medium)}>
                    {!isQuizStarted ? t("satsang_quiz.will_start_in") : t("satsang_quiz.has_started")}
                  </span>
                  <button
                    className={cn(
                      "w-auto flex justify-center items-center gap-2 px-10 py-2 rounded-full border border-borderPrimary bg-white z-10",
                      isQuizStarted && "cursor-pointer",
                    )}
                    onClick={() => {
                      if (isQuizStarted) {
                        handleOnPlayNowClick();
                      }
                    }}
                  >
                    {!isQuizStarted && quizData && (
                      <>
                        <Timer size={26} color={ICON_ORANGE_COLOR} />
                        <QuizCountdown date={quizData?.date} startTime={formatTimesForHHMM(quizData?.start_time ?? "21:00")} />
                      </>
                    )}
                    {isQuizStarted && (
                      <>
                        <FaRegCirclePlay size={20} color={ICON_ORANGE_COLOR} />
                        <span className="text-16 text-textColor font-inter font-semibold">Play Now</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Quiz title and source section */}
                <div className="w-full bg-cardBg p-4 rounded-xl flex justify-between md:px-10 items-center relative z-10">
                  <div className="w-auto flex flex-col justify-start items-start text-textColor font-baloobhai2Medium font-semibold z-10">
                    {quizData?.title ? (
                      <span className="text-xl text-textHeadingColor capitalize select-none" title={quizData?.title}>
                        {quizData?.title}
                      </span>
                    ) : (
                      <div className="w-[200px] h-4 bg-gray-200 rounded animate-pulse"></div>
                    )}
                    {quizData?.topic ? (
                      <span className="text-16 capitalize select-none" title={quizData?.topic}>
                        {quizData?.topic}
                      </span>
                    ) : (
                      <div className="w-[150px] h-4 bg-gray-200 rounded animate-pulse mt-2"></div>
                    )}
                  </div>
                  <div className={cn("w-auto flex flex-col gap-2 justify-center items-center z-10", quizData?.source_type ? "visible" : "invisible")}>
                    <div className="flex justify-center items-center gap-2">
                      {quizData?.source_type === "pdf" && (
                        <a
                          href={quizData?.source_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer"
                          title="PDF"
                        >
                          <PdfIcon fill="white" />
                        </a>
                      )}
                      {quizData?.source_type === "yt_video" && (
                        <a
                          href={`${youtubeLinkPrefix}${quizData?.source_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer"
                          title="Youtube"
                        >
                          <FaYoutube size={16} color="white" />
                        </a>
                      )}
                      {quizData?.source_type === "audio" && (
                        <button
                          className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer"
                          title="Audio"
                          onClick={() => handleOnAudioClick({ data: quizData })}
                        >
                          <AudioLines size={16} color="white" />
                        </button>
                      )}
                    </div>
                    <span className="text-12 font-inter font-normal text-textColor">Course</span>
                  </div>
                </div>
              </>
            ) : (
              <div className={cn("text-white text-xl z-10 text-center space-y-2", medium)}>
                <p>{t("satsang_quiz.noQuizMessage1")}</p>
                <p>{t("satsang_quiz.noQuizMessage2")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="w-full space-y-9">
        <div className="w-full flex justify-center items-center">
          <TabComponent tabs={tabs} selectedTab={selectedTab} onTabChange={(tab) => setSelectedTab(tab as TabKey)} />
        </div>
        <div>
          {selectedTab === "upcoming" && <UpcomingSyllabus mode={mode} />}
          {selectedTab === "completed" && <CompletedQuiz mode={mode} />}
        </div>
      </div>
    </div>
  );
}

export default QuizHome;
